import type { LucideIcon } from "lucide-react";
import { BarChart3, KanbanSquare, Rows3 } from "lucide-react";

export type TaskState = "pendiente" | "progreso" | "revision" | "completado";
export type TaskPriority = "baja" | "media" | "alta";
export type TaskView = "dashboard" | "kanban" | "tabla";
export type TaskParticipantPermission = "lector" | "editor" | "responsable";
export type TaskUserRole =
  | "super-admin"
  | "super_admin"
  | "admin"
  | "admin_rrhh"
  | "coordinador"
  | "tecnico"
  | "empleado"
  | string;

export type TaskUserSummary = {
  id: number;
  nombre: string;
  correo: string;
  rol: TaskUserRole;
};

export type TaskProjectSummary = {
  id: number;
  nombre: string;
};

export type TaskParticipant = {
  tarea_id: number;
  usuario_id: number;
  usuario_nombre: string | null;
  usuario_correo: string | null;
  permiso: TaskParticipantPermission;
  creado_en: string | null;
};

export type TaskSubtask = {
  id: number;
  tarea_id: number;
  titulo: string;
  completada: boolean;
  orden: number;
  creado_por: number | null;
  creado_por_nombre: string | null;
  creado_en: string | null;
  actualizado_en: string | null;
};

export type TaskComment = {
  id: number;
  tarea_id: number;
  usuario_id: number;
  usuario_nombre: string | null;
  comentario: string;
  mine: boolean;
  creado_en: string | null;
  actualizado_en: string | null;
};

export type TaskAttachment = {
  id: number;
  tarea_id: number;
  usuario_id: number;
  usuario_nombre: string | null;
  nombre_original: string;
  nombre_archivo: string;
  ruta: string;
  mime_type: string | null;
  tamano_bytes: number | null;
  creado_en: string | null;
  download_url: string;
};

export type TaskActivity = {
  id: number;
  tarea_id: number;
  usuario_id: number;
  usuario_nombre: string | null;
  accion: string;
  campo: string | null;
  valor_anterior: string | null;
  valor_nuevo: string | null;
  creado_en: string | null;
};

export type TaskTimeEntry = {
  id: number;
  tarea_id: number;
  usuario_id: number;
  usuario_nombre: string | null;
  inicio: string | null;
  fin: string | null;
  minutos: number | null;
  abierto: boolean;
  creado_en: string | null;
};

export type Task = {
  id: number;
  proyecto_id: number | null;
  proyecto_nombre: string | null;
  titulo: string;
  descripcion: string | null;
  estado: TaskState;
  prioridad: TaskPriority;
  fecha_inicio: string | null;
  fecha_fin: string | null;
  asignado_id: number | null;
  asignado_nombre: string | null;
  creado_por: number;
  creado_por_nombre: string | null;
  orden: number;
  creado_en: string | null;
  actualizado_en: string | null;
  subtareas_total: number;
  subtareas_completadas: number;
  comentarios_total: number;
  adjuntos_total: number;
  minutos_total: number;
  participantes: TaskParticipant[];
  subtareas: TaskSubtask[];
  comentarios: TaskComment[];
  adjuntos: TaskAttachment[];
  actividad: TaskActivity[];
  fichajes: TaskTimeEntry[];
};

export type TaskMeta = {
  current_user: TaskUserSummary;
  users: TaskUserSummary[];
  projects: TaskProjectSummary[];
  permissions: {
    can_create: boolean;
    can_reorder: boolean;
    can_assign: boolean;
    can_delete: boolean;
  };
};

export type TaskIndexResponse = {
  data: Task[];
  meta: TaskMeta;
};

export type TaskGroup = {
  key: TaskState;
  label: string;
  items: Task[];
};

export type TaskSectionConfig = {
  id: TaskView;
  title: string;
  subtitle: string;
  icon: LucideIcon;
};

export type TaskActorCapabilities = {
  isManager: boolean;
  canChangeState: boolean;
  canAssign: boolean;
  canManageParticipants: boolean;
  canManageSubtasks: boolean;
  canComment: boolean;
  canUploadAttachments: boolean;
  canDeleteAttachments: boolean;
  canTrackTime: boolean;
  canStartTime: boolean;
  canStopTime: boolean;
  canDeleteTask: boolean;
  nextStates: TaskState[];
  availableStates: TaskState[];
};

export const taskStateOrder: TaskState[] = [
  "pendiente",
  "progreso",
  "revision",
  "completado",
];

export const taskStateLabels: Record<TaskState, string> = {
  pendiente: "Pendiente",
  progreso: "En progreso",
  revision: "Revisión",
  completado: "Completado",
};

export const taskPriorityLabels: Record<TaskPriority, string> = {
  baja: "Baja",
  media: "Media",
  alta: "Alta",
};

export const taskViewSections: TaskSectionConfig[] = [
  {
    id: "kanban",
    title: "Kanban",
    subtitle: "Vista Kanban organizada por filas de trabajo",
    icon: KanbanSquare,
  },
  {
    id: "tabla",
    title: "Tabla",
    subtitle: "Vista de tabla organizada por columnas",
    icon: Rows3,
  },
  {
    id: "dashboard",
    title: "Dashboard",
    subtitle: "Métricas, actividad y foco operativo",
    icon: BarChart3,
  },
];

export function normalizeTaskRole(role: string | null | undefined): string {
  if (!role) return "empleado";
  if (role === "super_admin") return "super-admin";
  return role;
}

export function getTaskStateLabel(value: TaskState | null | undefined): string {
  if (!value) return "Sin estado";
  return taskStateLabels[value] ?? value;
}

export function getTaskPriorityLabel(
  value: TaskPriority | null | undefined,
): string {
  if (!value) return "Sin prioridad";
  return taskPriorityLabels[value] ?? value;
}

export function formatTaskDate(value: string | null | undefined): string {
  if (!value) return "—";

  return new Date(value).toLocaleDateString("es-ES", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
}

export function formatTaskDateTime(value: string | null | undefined): string {
  if (!value) return "—";

  return new Date(value).toLocaleString("es-ES", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export function formatTaskMinutes(value: number | null | undefined): string {
  if (!value) return "0m";

  const hours = Math.floor(value / 60);
  const minutes = value % 60;

  if (hours <= 0) return `${minutes}m`;
  if (minutes <= 0) return `${hours}h`;

  return `${hours}h ${minutes}m`;
}

export function formatTaskFileSize(value: number | null | undefined): string {
  if (!value) return "Archivo";
  if (value < 1024) return `${value} B`;
  if (value < 1024 * 1024) return `${(value / 1024).toFixed(1)} KB`;
  return `${(value / 1024 / 1024).toFixed(1)} MB`;
}

export function isTaskOverdue(task: Task): boolean {
  if (!task.fecha_fin || task.estado === "completado") return false;
  const end = new Date(task.fecha_fin);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return end.getTime() < today.getTime();
}

export function buildTaskGroups(tasks: Task[]): TaskGroup[] {
  return taskStateOrder.map((state) => ({
    key: state,
    label: getTaskStateLabel(state),
    items: [...tasks]
      .filter((task) => task.estado === state)
      .sort((left, right) => {
        if (left.orden !== right.orden) return left.orden - right.orden;
        return right.id - left.id;
      }),
  }));
}

export function getTaskActorCapabilities(
  task: Task,
  meta: TaskMeta,
): TaskActorCapabilities {
  const role = normalizeTaskRole(meta.current_user.rol);
  const participant = task.participantes.find(
    (item) => item.usuario_id === meta.current_user.id,
  );
  const isManager = ["super-admin", "admin", "admin_rrhh", "coordinador"].includes(
    role,
  );
  const isResponsible = task.asignado_id === meta.current_user.id;
  const isCreator = task.creado_por === meta.current_user.id;
  const participantCanEdit =
    participant?.permiso === "editor" || participant?.permiso === "responsable";
  const hasAnyTimeEntry = task.fichajes.length > 0;
  const hasOpenTimeEntry = task.fichajes.some(
    (entry) => entry.abierto && entry.usuario_id === meta.current_user.id,
  );

  const transitions: Record<TaskState, TaskState[]> = {
    pendiente: isResponsible ? ["progreso"] : [],
    progreso: isResponsible ? ["revision"] : [],
    revision: isManager || isCreator ? ["completado"] : [],
    completado: [],
  };
  const availableStates = transitions[task.estado] ?? [];

  return {
    isManager,
    canChangeState: availableStates.length > 0,
    canAssign: isManager || isCreator,
    canManageParticipants: isManager || isResponsible,
    canManageSubtasks:
      isManager ||
      isResponsible ||
      participantCanEdit ||
      (isCreator && Boolean(participant)),
    canComment: isManager || isResponsible || isCreator || Boolean(participant),
    canUploadAttachments:
      isManager || isResponsible || isCreator || participantCanEdit,
    canDeleteAttachments:
      isManager || isResponsible || isCreator || participantCanEdit,
    canTrackTime: isResponsible,
    canStartTime: isResponsible && task.estado === "progreso" && !hasAnyTimeEntry,
    canStopTime: isResponsible && hasOpenTimeEntry,
    canDeleteTask: isCreator,
    nextStates: availableStates,
    availableStates,
  };
}
